import type { ZudokuConfig, ZudokuPlugin } from "zudoku";
import { navigation } from "./navigation";

const SITE_URL = "https://docs.omaxtelecom.com";
const SITE_NAME = "OmaxTelecom Partner Documentation";
const DEFAULT_DESCRIPTION =
  "Partner documentation for the OmaxTelecom Console. Step-by-step guides for billing, whitelabel storefronts, Bappy, Telkor, voice services, and account management.";

const lightOnlyPlugin: ZudokuPlugin = {
  getHead: () => (
    <script
      dangerouslySetInnerHTML={{
        __html: `
          try {
            localStorage.setItem("theme", "light");
            document.documentElement.classList.remove("dark");
            document.documentElement.style.colorScheme = "light";
          } catch {}
        `,
      }}
    />
  ),
};

const seoPlugin: ZudokuPlugin = {
  getHead: () => (
    <>
      <meta
        name="robots"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />
      <meta name="googlebot" content="index, follow" />
      {(process.env.ZUDOKU_PUBLIC_GOOGLE_SITE_VERIFICATION ??
        process.env.GOOGLE_SITE_VERIFICATION) ? (
        <meta
          name="google-site-verification"
          content={
            process.env.ZUDOKU_PUBLIC_GOOGLE_SITE_VERIFICATION ??
            process.env.GOOGLE_SITE_VERIFICATION
          }
        />
      ) : null}
      <link rel="alternate" hrefLang="en" href={SITE_URL} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:url" content={SITE_URL} />
      <meta property="og:title" content={SITE_NAME} />
      <meta property="og:description" content={DEFAULT_DESCRIPTION} />
      <meta property="og:image" content={`${SITE_URL}/banner.svg`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={SITE_NAME} />
      <meta name="twitter:description" content={DEFAULT_DESCRIPTION} />
      <meta name="twitter:image" content={`${SITE_URL}/banner.svg`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "@id": `${SITE_URL}/#organization`,
                name: "OmaxTelecom",
                url: "https://www.omaxtelecom.com",
                logo: `${SITE_URL}/omaxtelecom_logo_color.svg`,
              },
              {
                "@type": "WebSite",
                "@id": `${SITE_URL}/#website`,
                url: SITE_URL,
                name: SITE_NAME,
                description: DEFAULT_DESCRIPTION,
                publisher: { "@id": `${SITE_URL}/#organization` },
                inLanguage: "en",
              },
              {
                "@type": "WebPage",
                "@id": `${SITE_URL}/#webpage`,
                url: SITE_URL,
                name: SITE_NAME,
                description: DEFAULT_DESCRIPTION,
                isPartOf: { "@id": `${SITE_URL}/#website` },
                about: { "@id": `${SITE_URL}/#organization` },
                inLanguage: "en",
              },
            ],
          }),
        }}
      />
    </>
  ),
};

const config: ZudokuConfig = {
  site: {
    title: "OmaxTelecom Partner Documentation",
    logo: {
      src: {
        light: "/omaxtelecom_logo_color.svg",
        dark: "/omaxtelecom_logo_color.svg",
      },
      alt: "OmaxTelecom",
      width: "130px",
    },
    footer: {
      copyright: `© ${new Date().getFullYear()} OmaxTelecom. All rights reserved.`,
    },
    showPoweredBy: false,
  },
  metadata: {
    title: "%s | OmaxTelecom Partner Docs",
    defaultTitle: SITE_NAME,
    description: DEFAULT_DESCRIPTION,
    favicon: "/favicon.png",
    keywords: [
      "OmaxTelecom",
      "OmaxTelecom Console",
      "partner documentation",
      "whitelabel eSIM",
      "Bappy",
      "Telkor",
      "partner portal",
      "billing",
      "wallet top-up",
      "OmaxPhone",
      "physical SIM",
      "storefront settings",
    ],
    authors: ["OmaxTelecom"],
    creator: "OmaxTelecom",
    publisher: "OmaxTelecom",
    applicationName: SITE_NAME,
    generator: "Zudoku",
    referrer: "origin-when-cross-origin",
  },
  canonicalUrlOrigin: SITE_URL,
  sitemap: {
    siteUrl: SITE_URL,
    changefreq: "weekly",
    priority: 0.8,
    autoLastmod: true,
    exclude: ["/400", "/404", "/500"],
  },
  plugins: [lightOnlyPlugin, seoPlugin],
  theme: {
    fonts: {
      sans: {
        url: "https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,100..900&display=swap",
        fontFamily: '"Inter Variable", Inter, sans-serif',
      },
      mono: "JetBrains Mono",
    },
    light: {
      background: "oklch(100% 0 none)",
      foreground: "oklch(12.9% 0.027 262)",
      card: "oklch(100% 0 none)",
      cardForeground: "oklch(12.9% 0.027 262)",
      popover: "oklch(100% 0 none)",
      popoverForeground: "oklch(12.9% 0.027 262)",
      primary: "oklch(54.1% 0.247 293)",
      primaryForeground: "oklch(98.4% 0.002 248)",
      secondary: "oklch(96.7% 0.003 265)",
      secondaryForeground: "oklch(21% 0.032 265)",
      muted: "oklch(96.7% 0.003 265)",
      mutedForeground: "oklch(40% 0.023 264)",
      accent: "oklch(96.7% 0.003 265)",
      accentForeground: "oklch(21% 0.032 265)",
      destructive: "oklch(63.7% 0.208 25.3)",
      destructiveForeground: "oklch(98.4% 0.002 248)",
      border: "oklch(92.8% 0.006 265)",
      input: "oklch(92.8% 0.006 265)",
      ring: "oklch(54.1% 0.247 293)",
      radius: "0.6rem",
    },
    customCss: {
      "@layer base": {
        html: {
          colorScheme: "light",
        },
        body: {
          color: "oklch(12.9% 0.027 262)",
        },
        "html.dark, .dark": {
          colorScheme: "light",
          "--dark": "-1",
          "--background": "oklch(100% 0 none)",
          "--foreground": "oklch(12.9% 0.027 262)",
          "--card": "oklch(100% 0 none)",
          "--card-foreground": "oklch(12.9% 0.027 262)",
          "--popover": "oklch(100% 0 none)",
          "--popover-foreground": "oklch(12.9% 0.027 262)",
          "--primary": "oklch(54.1% 0.247 293)",
          "--primary-foreground": "oklch(98.4% 0.002 248)",
          "--secondary": "oklch(96.7% 0.003 265)",
          "--secondary-foreground": "oklch(21% 0.032 265)",
          "--muted": "oklch(96.7% 0.003 265)",
          "--muted-foreground": "oklch(40% 0.023 264)",
          "--accent": "oklch(96.7% 0.003 265)",
          "--accent-foreground": "oklch(21% 0.032 265)",
          "--destructive": "oklch(63.7% 0.208 25.3)",
          "--destructive-foreground": "oklch(98.4% 0.002 248)",
          "--border": "oklch(92.8% 0.006 265)",
          "--input": "oklch(92.8% 0.006 265)",
          "--ring": "oklch(54.1% 0.247 293)",
          "--tw-prose-invert-body": "oklch(21% 0.032 265)",
          "--tw-prose-invert-headings": "oklch(12.9% 0.027 262)",
          "--tw-prose-invert-lead": "oklch(37.1% 0 0)",
          "--tw-prose-invert-links": "oklch(54.1% 0.247 293)",
          "--tw-prose-invert-bold": "oklch(12.9% 0.027 262)",
          "--tw-prose-invert-counters": "oklch(40% 0.023 264)",
          "--tw-prose-invert-bullets": "oklch(75% 0.006 265)",
          "--tw-prose-invert-hr": "oklch(92.8% 0.006 265)",
          "--tw-prose-invert-quotes": "oklch(21% 0.032 265)",
          "--tw-prose-invert-quote-borders": "oklch(92.8% 0.006 265)",
          "--tw-prose-invert-captions": "oklch(40% 0.023 264)",
          "--tw-prose-invert-code": "oklch(12.9% 0.027 262)",
          "--tw-prose-invert-pre-code": "oklch(12.9% 0.027 262)",
          "--tw-prose-invert-pre-bg": "oklch(96.7% 0.003 265)",
          "--tw-prose-invert-th-borders": "oklch(92.8% 0.006 265)",
          "--tw-prose-invert-td-borders": "oklch(92.8% 0.006 265)",
        },
        "html.dark .prose, .dark .prose": {
          "--tw-prose-body": "oklch(21% 0.032 265)",
          "--tw-prose-headings": "oklch(12.9% 0.027 262)",
          "--tw-prose-lead": "oklch(37.1% 0 0)",
          "--tw-prose-links": "oklch(54.1% 0.247 293)",
          "--tw-prose-bold": "oklch(12.9% 0.027 262)",
          "--tw-prose-counters": "oklch(40% 0.023 264)",
          "--tw-prose-bullets": "oklch(75% 0.006 265)",
          "--tw-prose-hr": "oklch(92.8% 0.006 265)",
          "--tw-prose-quotes": "oklch(21% 0.032 265)",
          "--tw-prose-quote-borders": "oklch(92.8% 0.006 265)",
          "--tw-prose-captions": "oklch(40% 0.023 264)",
          "--tw-prose-code": "oklch(12.9% 0.027 262)",
          "--tw-prose-pre-code": "oklch(12.9% 0.027 262)",
          "--tw-prose-pre-bg": "oklch(96.7% 0.003 265)",
          "--tw-prose-th-borders": "oklch(92.8% 0.006 265)",
          "--tw-prose-td-borders": "oklch(92.8% 0.006 265)",
          color: "oklch(21% 0.032 265)",
        },
      },
      "header button[aria-label^='Switch to'], header button.group.rounded-full.border, li:has(> button[aria-label^='Switch to'])":
        {
          display: "none !important",
        },
    },
  },
  search: {
    type: "pagefind",
    maxSubResults: 3,
    ranking: {
      termFrequency: 0.8,
      pageLength: 0.6,
      termSimilarity: 1.2,
      termSaturation: 1.2,
    },
  },
  navigation,
  redirects: [
    { from: "/", to: "/getting-started/introduction" },
    { from: "/data/whitelabel/dashboard-reports", to: "/data/whitelabel/dashboard" },
    { from: "/data/whitelabel/transactions-customers", to: "/data/whitelabel/transactions" },
    { from: "/data/whitelabel/growth-programs", to: "/data/whitelabel/referrals" },
    {
      from: "/data/whitelabel/storefront/pricing-promotions",
      to: "/data/whitelabel/storefront/pricing-settings",
    },
    {
      from: "/data/bappy/sms-notifications",
      to: "/data/bappy/notifications",
    },
    {
      from: "/data/telkor/links-sims",
      to: "/data/telkor/links",
    },
    {
      from: "/data/telkor/plans-coverages",
      to: "/data/telkor/plans",
    },
    {
      from: "/data/telkor/pricing",
      to: "/data/telkor/coverages",
    },
    {
      from: "/api-access/api-keys",
      to: "/api-access/overview",
    },
  ],
};

export default config;
